import { 
  Dialog, 
  SubheaderText, 
  Subheader,
} from "@ifca-ui/core";
import { Box, Typography, TextField, Autocomplete } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useGetAllJobPositionLazyQuery, useSubmitCompanyCommissionTeamDetailMutation } from "generated/graphql";
import { useParams } from "react-router";
import { useEffect, useState } from "react";
import { setSnackBar } from "helpers/snackBarSubjectHelper";
import { SystemMsgs } from "helpers/Messages/SystemMsg";
import moment from "moment";

const PRINCIPAL_OPTIONS = [
  {
    "name":"Internal",
    "value":"Internal"
  },
  {
    "name":"Agent",
    "value":"Agent"
  },
]


interface DetailFormDialogProps {
  show: boolean;
  mode: 'New' | 'Edit'
  data: any;
  onClose: () => void;
  onCompleted: () => void;
}


interface PrincipalOption {
  name: string;
  value: string;
}
interface PositionOption {
  id: string;
  position: string;
}

interface FormValues {
  principal: PrincipalOption | null;
  position: PositionOption | null;
}

const transformPositionId = (data: any): string[] => {
    let result: string[] = []
    
    if(Array.isArray(data)) {
        data.forEach(({positionId, children}) => {
            result = [ ...result, positionId, ...transformPositionId(children) ];
        })
    } else if (typeof data == 'object') {
      const { positionId, children } = data
      result = [positionId, ...transformPositionId(children)]
    }

    return result;
}

const DetailFormDialog = ({show, data, mode, onClose, onCompleted}: DetailFormDialogProps)=>{
    const isEditMode = mode === 'Edit';
    const {control, 
      handleSubmit, 
      formState: { errors },
      watch
    } = useForm<FormValues>({
      defaultValues: {
        principal: isEditMode ? PRINCIPAL_OPTIONS.find((el) => el.value === data?.principal) : null,
        position: isEditMode ? data?.position : null,
      }
    });
    const {projectID} = useParams()
    const [jobPositionOptions, setJobPositionOptions] = useState([])

    const [getAllJobPosition] = useGetAllJobPositionLazyQuery({onCompleted(resultData) {
      if(!resultData.getAllJobPosition?.jobPositions) return;
      let jobPositions = resultData.getAllJobPosition?.jobPositions
        
      if(!data?.isSub && mode === 'New') {
        const positionIds = transformPositionId(data?.details || data)
        jobPositions = resultData.getAllJobPosition?.jobPositions.filter(({id}) => !positionIds.includes(id))
      } else if(data?.isSub && mode === 'New') {
        let positionIds = transformPositionId(data?.parentData?.details)
        jobPositions = resultData.getAllJobPosition?.jobPositions.filter(({id}) => !positionIds.includes(id))  
      }
      
      if(mode === 'Edit') {
        let positionIds = transformPositionId(data?.parentData?.details)
        const jobPositionsNotAssign = resultData.getAllJobPosition?.jobPositions.filter(({id}) => !positionIds.includes(id))  
        const jobPositionTheirOwn = jobPositions.filter(({id}) => id == data?.positionId)
        jobPositions = [...jobPositionsNotAssign, ...jobPositionTheirOwn]
      }
      setJobPositionOptions(jobPositions?.map(jp => ({id: jp.id, position: jp.position})))
    }})


    const [submitCompanyCommissionTeamDetail] = useSubmitCompanyCommissionTeamDetailMutation({onCompleted(resultData, clientOptions) {
      if(resultData.submitCompanyCommissionTeamDetail){
        setSnackBar(mode === 'New' ?  SystemMsgs.createNewRecord() : SystemMsgs.updateRecord());
        onCompleted()
      }
    }})

    const isExistAgentPrincipalFunc = (data: Array<any>): boolean => {
      for (let item of data) {
        if(item.principal === 'Agent') {
          return true
        }

        if(item.children && item.children.length) {
          const foundPrincipalAgent = isExistAgentPrincipalFunc(item.children)
          if(foundPrincipalAgent) return true
        }
      }

      return false;
    }

    const onSubmit = (submittedData: FormValues) => {
      const isSub = data?.isSub;
      const isNewMode = mode === 'New';
      const { principal, position } = submittedData

      const isExistAgentPrincipal = isExistAgentPrincipalFunc(data?.details || data?.parentData?.details)

      if(!isNewMode && data?.principal == 'Agent') {
        onClose()
        return;
      }

      if(isExistAgentPrincipal && principal.value === 'Agent') {
        setSnackBar('Principal Agent is have been created')
        return;
      }
    
      const input = {
        id: isNewMode ? null : data?.id,
        parentId: isNewMode ? (isSub ? data?.id : null) : data?.parentId,
        positionId: principal?.value === 'Internal' ? position?.id : null,
        principal: principal?.value,
        companyCommissionTeamId: isNewMode
          ? isSub
            ? data?.companyCommissionTeamId
            : data?.id
          : data?.companyCommissionTeamId,
        level: isNewMode ? (isSub ? Number(data?.level) + 1 : 1) : data?.level,
      };
    
      submitCompanyCommissionTeamDetail({ variables: { input } });
    };

    useEffect(()=> {
      getAllJobPosition({variables: {
        projectId: projectID
      }})
    },[])

    return (
      <Dialog fullWidth={true} open={show} onClose={onClose}
        header={
          <Subheader sx={{ padding: '10px 12px' }}>
            <SubheaderText
              primary={
                <Box borderBottom="1px solid #fff">
                  <Box display="flex" margin="2px 0px" width="100%">
                    <Typography
                      className="text-xsTitle"
                      color="#FF9800"
                      component="span"
                      flex="1 1"
                      variant="inherit"
                    >
                      Company Commission Structure
                    </Typography>
                    <Typography
                      className="text-xsTitle"
                      color="#000"
                      component="span"
                      flex="1 1"
                      justifyContent={"flex-end"}
                      display={"flex"}
                      variant="inherit"
                    >
                      {mode}
                    </Typography>
                  </Box>
                </Box>
              }
              secondary={
                <Box>
                  <Box display="flex" margin="2px 0px" width="100%">
                    <Typography
                      className="text-xsTitle"
                      color="common.black"
                      component="span"
                      flex="1 1"
                      variant="inherit"
                    >
                      {moment(new Date(data?.generateDate)).format("D MMMM YYYY")}
                    </Typography>
                    <Typography
                      className="text-xsTitle"
                      color="common.black"
                      component="span"
                      variant="inherit"
                    >
                      
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </Subheader>
          }
        body={
          <Box width="100%" display='flex' flexDirection='column' gap='16px'> 
            <Controller
              name='principal'
              control={control}
              rules={{ required: "Principal is required" }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={PRINCIPAL_OPTIONS}
                  fullWidth
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Internal/Agent"
                      variant='standard'
                      required
                      size='small'
                      helperText={errors?.principal?.message ?? ""}
                      error={!!errors?.principal?.message}
                    />
                  )}
                  onChange={(_, value) => field.onChange(value ?? null)}
                />
              )}
            />
            {watch('principal')?.value === 'Internal' && (
              <Controller
                name='position'
                control={control}
                rules={{ required: "Job Position is required" }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={jobPositionOptions}
                    fullWidth
                    getOptionLabel={(option) => option.position || ""}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Job Position"
                        variant='standard'
                        required
                        size='small'
                        helperText={errors?.position?.message ?? ""}
                        error={!!errors?.position?.message}
                      />
                    )}
                    onChange={(_, value) => field.onChange(value ?? null)}
                  />
                )}
              />
            )}
            {watch('principal')?.value === 'Agent' && (
              <Box>
                <TextField
                  value="Property Agency"
                  variant='standard'
                  required
                  size='small'
                  fullWidth
                  disabled
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#000000de',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: '#000000de',
                    },
                    '& .MuiInput-underline.Mui-disabled:before': {
                      borderBottomColor: '#949494',
                      borderBottomStyle: 'double'
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        }
        footer={{
          buttons: [
            {
              children: 'Cancel',
              color: 'primary',
              onClick: () => {
                onClose()
              },
            },
            {
              children: 'Save',
              color: 'primary',
              onClick: () =>{
                handleSubmit(onSubmit)()
              } 
            },
          ],

        }}
      >
      </Dialog>
    )
}

export default DetailFormDialog;